import { clsx } from "clsx";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useState, useEffect, useRef } from "react";

import LogoFull from "../assets/logo-full.svg?react";
import { useNavigateToLink } from "../hooks/useNavigateToLink";
import { ROUTES } from "../routes";

import { WIP_ENABLED_USE_CASES } from "./constants";
import Link from "./Link";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { useReducedMotion } from "./ui/use-reduced-motion";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const exploreCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigateToLink = useNavigateToLink();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = (e: React.MouseEvent, routeUrl: string) => {
    setMobileMenuOpen(false);
    setIsExploreOpen(false);

    navigateToLink(e, routeUrl);
  };

  const handleExploreMouseEnter = () => {
    if (exploreCloseTimeoutRef.current) {
      clearTimeout(exploreCloseTimeoutRef.current);
      exploreCloseTimeoutRef.current = null;
    }
    setIsExploreOpen(true);
  };

  const handleExploreMouseLeave = () => {
    exploreCloseTimeoutRef.current = setTimeout(() => {
      setIsExploreOpen(false);
    }, 150);
  };

  const exploreMenuContent = {
    useCases: [
      {
        title: "Founders",
        description: "Ship your product vision faster",
        href: ROUTES.founders,
      },
      {
        title: "Product Managers",
        description: "Move from spec to design instantly",
        href: ROUTES.useCases,
      },
      {
        title: "Designers",
        description: "Spend more time with users",
        href: ROUTES.designers,
      },
      {
        title: "Marketers",
        description: "Create landing pages in minutes",
        href: ROUTES.marketers,
      },
      {
        title: "Sales",
        description: "Demo custom solutions on the fly",
        href: ROUTES.sales,
      },
    ],
    resources: [
      {
        title: "MCP server",
        description: "Design from Claude Code, Cursor and more",
        href: ROUTES.mcp,
      },
      {
        title: "Changelog",
        description: "What's new in Flowstep",
        href: ROUTES.changelog,
      },
      {
        title: "About",
        description: "Learn more about Flowstep",
        href: ROUTES.about,
      },
      {
        title: "Affiliates",
        description: "Refer and earn commission",
        href: ROUTES.affiliates,
      },
      {
        title: "Blog",
        description: "Insights on product design",
        href: ROUTES.blog,
      },
    ],
  };

  return (
    <>
      <header
        className={clsx(
          "fixed top-0 z-50 w-full transition-all duration-300",
          isScrolled
            ? "bg-background/95 border-b shadow-sm backdrop-blur-[12px]"
            : "bg-background/60 backdrop-blur-[12px]",
        )}
      >
        <div className="w-full px-4 md:px-8 lg:px-16">
          <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between">
            {/* Left side: Logo + Nav */}
            <div className="flex items-center gap-6 lg:gap-8">
              {/* Logo */}
              <a
                href={ROUTES.homeTop}
                onClick={(e) => navigateTo(e, ROUTES.homeTop)}
                aria-label="Flowstep home"
              >
                <LogoFull className="text-[#0247FE] dark:text-white" />
              </a>

              {/* Desktop Navigation */}
              <nav className="hidden items-center gap-6 md:flex lg:gap-7">
                {/* Explore Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={handleExploreMouseEnter}
                  onMouseLeave={handleExploreMouseLeave}
                >
                  <button className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-[13px] transition-colors">
                    Explore
                    <ChevronDown
                      className={clsx(
                        "h-3 w-3 transition-transform duration-200",
                        isExploreOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isExploreOpen && (
                    <div
                      className={clsx(
                        "bg-background animate-in fade-in zoom-in-95 absolute left-0 mt-2 rounded-lg border shadow-lg backdrop-blur-[12px] duration-200",
                        WIP_ENABLED_USE_CASES ? "w-[560px]" : "w-[280px]",
                      )}
                    >
                      <div
                        className={clsx(
                          "grid divide-x",
                          WIP_ENABLED_USE_CASES ? "grid-cols-2" : "grid-cols-1",
                        )}
                      >
                        {WIP_ENABLED_USE_CASES && (
                          <MenuSectionDesktop
                            title="Use Cases"
                            items={exploreMenuContent.useCases}
                            navigateTo={navigateTo}
                          />
                        )}
                        <MenuSectionDesktop
                          title="Resources"
                          items={exploreMenuContent.resources}
                          navigateTo={navigateTo}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Link
                  href={ROUTES.pricing}
                  className="text-muted-foreground hover:text-foreground text-[13px] transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  href={ROUTES.docs}
                  className="text-muted-foreground hover:text-foreground text-[13px] transition-colors"
                  external
                >
                  Docs
                </Link>
              </nav>
            </div>

            {/* Right side: CTA Buttons + Mobile Menu */}
            <div className="flex items-center gap-3">
              {/* Desktop CTA Buttons */}
              <div className="hidden items-center gap-3 md:flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 cursor-pointer px-4 text-[13px]"
                  onClick={(e: React.MouseEvent) => navigateTo(e, ROUTES.login)}
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  className="bg-foreground text-background hover:bg-foreground/90 h-9 cursor-pointer px-4 text-[13px]"
                  onClick={(e: React.MouseEvent) =>
                    navigateTo(e, ROUTES.register)
                  }
                >
                  Get Started
                </Button>
              </div>

              {/* Mobile Menu Button - Enhanced touch target */}
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 touch-manipulation transition-transform active:scale-95 md:hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Toggle menu"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        {/* The sheet portals out of the page, so it scopes itself dark to
            match the dark pages instead of inheriting a light root. */}
        <SheetContent
          side="right"
          className="dark bg-background text-foreground w-full border-white/10 p-0"
          showClose={false}
        >
          {/* Hidden but accessible title and description */}
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SheetDescription className="sr-only">
            Browse Flowstep use cases, resources, and pricing
          </SheetDescription>

          {/* Menu Header */}
          <SheetHeader className="flex h-16 flex-row items-center justify-between border-b px-6">
            <a
              href={ROUTES.homeTop}
              onClick={(e) => navigateTo(e, ROUTES.homeTop)}
              aria-label="Flowstep home"
            >
              <LogoFull className="text-[#0247FE] dark:text-white" />
            </a>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-9 cursor-pointer px-4 text-[13px]"
                onClick={(e) => navigateTo(e, ROUTES.login)}
              >
                Sign In
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </SheetHeader>

          {/* Menu Content */}
          <div
            className="overflow-y-auto px-6 py-8"
            style={{ maxHeight: "calc(100vh - 64px)" }}
          >
            {/* Product Section */}
            <div className="mb-10">
              <h3 className="text-muted-foreground mb-6 text-xs tracking-wider uppercase">
                Product
              </h3>
              <div className="space-y-5">
                <Link
                  href={ROUTES.pricing}
                  className="text-foreground hover:text-primary block text-base transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  href={ROUTES.docs}
                  external
                  className="text-foreground hover:text-primary block text-base transition-colors"
                >
                  Docs
                </Link>
              </div>
            </div>

            <MenuSectionMobile
              title="Resources"
              items={exploreMenuContent.resources}
              navigateTo={navigateTo}
            />
            {WIP_ENABLED_USE_CASES && (
              <MenuSectionMobile
                title="Use Cases"
                items={exploreMenuContent.useCases}
                navigateTo={navigateTo}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

type MenuSectionProps = {
  title: string;
  items: {
    title: string;
    description: string;
    href: string;
  }[];
  navigateTo: (e: React.MouseEvent, routeUrl: string) => void;
};

function MenuSectionDesktop({ title, items, navigateTo }: MenuSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const [hoveredHref, setHoveredHref] = useState<string | null>(null);

  return (
    <div className="p-6">
      <h3 className="text-muted-foreground mb-4 text-[11px] tracking-wide uppercase">
        {title}
      </h3>
      <div className="space-y-1" onMouseLeave={() => setHoveredHref(null)}>
        {items.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={(e) => navigateTo(e, item.href)}
            onMouseEnter={() => setHoveredHref(item.href)}
            className="relative block rounded-md px-3 py-2.5"
          >
            {/* Sliding hover highlight — one pill glides between items */}
            {hoveredHref === item.href && (
              <motion.span
                aria-hidden
                layoutId={`menu-hover-${title}`}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { type: "spring", bounce: 0.2, duration: 0.4 }
                }
                className="bg-accent/50 absolute inset-0 rounded-md"
              />
            )}
            <div className="text-foreground relative text-[13px]">
              {item.title}
            </div>
            <div className="text-muted-foreground relative mt-0.5 text-[11px]">
              {item.description}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function MenuSectionMobile({ title, items, navigateTo }: MenuSectionProps) {
  return (
    <div className="mb-10">
      <h3 className="text-muted-foreground mb-6 text-xs tracking-wider uppercase">
        {title}
      </h3>
      <div className="space-y-5">
        {items.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={(e) => navigateTo(e, item.href)}
            className="text-foreground hover:text-primary block text-base transition-colors"
          >
            {item.title}
          </a>
        ))}
      </div>
    </div>
  );
}
